import type {
  JourneyDraftState,
  JourneyResponse,
  LiveTriageDraftState,
  LiveTriageStepSnapshot,
  LiveTriageStepState,
} from '@core/entities/JourneyDraftState';
import type { TriageAnswerRecord, TriageQuestionnaire, TriageWorkflowStepIdentity } from '@core/entities/Triage';

const storageKey = 'inav.dt02.journey-draft';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function createInitialState(recoveryMessage?: string): JourneyDraftState {
  return {
    steps: {},
    recoveryMessage,
  };
}

function cloneSerializable<T>(value: T): T {
  return structuredClone(value);
}

function toStoredQuestionnaire(questionnaire: TriageQuestionnaire): TriageQuestionnaire {
  const { source: _source, ...storedQuestionnaire } = questionnaire;
  return cloneSerializable(storedQuestionnaire);
}

function cloneState(state: JourneyDraftState): JourneyDraftState {
  return {
    recoveryMessage: state.recoveryMessage,
    steps: Object.fromEntries(
      Object.entries(state.steps).map(([path, step]) => [path, {
        previousPath: step.previousPath,
        response: step.response
          ? {
            question: step.response.question,
            answer: step.response.answer,
            value: step.response.value,
          }
          : undefined,
      }])
    ),
    liveTriage: state.liveTriage
      ? {
        activeStepId: state.liveTriage.activeStepId,
        steps: Object.fromEntries(
          Object.entries(state.liveTriage.steps).map(([stepId, step]) => [stepId, {
            previousStepId: step.previousStepId,
            questionnaire: toStoredQuestionnaire(step.questionnaire),
            answers: cloneSerializable(step.answers),
          } satisfies LiveTriageStepState])
        ),
      }
      : undefined,
  };
}

function isLiveTriageDraftState(value: unknown): value is LiveTriageDraftState {
  return isRecord(value) && isRecord(value.steps);
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

function hasValidWorkflowStepIdentity(questionnaire: TriageQuestionnaire): questionnaire is TriageQuestionnaire & { workflowStepIdentity: TriageWorkflowStepIdentity } {
  const workflowStepIdentity = questionnaire.workflowStepIdentity;
  return isRecord(workflowStepIdentity)
    && isPositiveInteger(workflowStepIdentity.journeyRevision)
    && isPositiveInteger(workflowStepIdentity.stepSequence)
    && isPositiveInteger(workflowStepIdentity.questionnaireEventSequence);
}

export class JourneyDraftStore {
  private state = this.hydrate();

  public clear(): void {
    this.state = createInitialState();
    this.persist();
  }

  public getSnapshot(): JourneyDraftState {
    return cloneState(this.state);
  }

  public consumeRecoveryMessage(): string | undefined {
    const { recoveryMessage } = this.state;
    if (!recoveryMessage) {
      return undefined;
    }

    this.state = {
      ...this.state,
      recoveryMessage: undefined,
    };
    this.persist();
    return recoveryMessage;
  }

  public getPreviousPath(path: string): string | undefined {
    return this.state.steps[path]?.previousPath;
  }

  public getResponse(path: string): JourneyResponse | undefined {
    return this.state.steps[path]?.response;
  }

  public getActiveLiveTriageStep(): LiveTriageStepSnapshot | undefined {
    const activeStepId = this.state.liveTriage?.activeStepId;
    if (!activeStepId) {
      return undefined;
    }

    const step = this.state.liveTriage?.steps[activeStepId];
    if (!this.isValidLiveTriageStep(step)) {
      this.markCorruptedState();
      return undefined;
    }

    return {
      stepId: activeStepId,
      previousStepId: step.previousStepId,
      questionnaire: cloneSerializable(step.questionnaire),
      answers: cloneSerializable(step.answers),
    };
  }

  public initializeLiveTriageStep(stepId: string, questionnaire: TriageQuestionnaire): void {
    this.state = {
      ...this.state,
      liveTriage: {
        activeStepId: stepId,
        steps: {
          [stepId]: {
            questionnaire: toStoredQuestionnaire(questionnaire),
            answers: {},
          },
        },
      },
    };

    this.persist();
  }

  public advanceLiveTriageStep(currentStepId: string, nextStepId: string, questionnaire: TriageQuestionnaire): void {
    const currentStep = this.state.liveTriage?.steps[currentStepId];
    const liveTriageSteps = this.state.liveTriage?.steps;
    const existingNextStep = this.state.liveTriage?.steps[nextStepId];
    const nextAnswers = existingNextStep?.answers ?? {};

    if (currentStepId === nextStepId && currentStep) {
      this.state = {
        ...this.state,
        liveTriage: {
          activeStepId: nextStepId,
          steps: {
            ...liveTriageSteps,
            [nextStepId]: {
              ...currentStep,
              questionnaire: toStoredQuestionnaire(questionnaire),
              answers: cloneSerializable(nextAnswers),
            },
          },
        },
      };

      this.persist();
      return;
    }

    this.state = {
      ...this.state,
      liveTriage: {
        activeStepId: nextStepId,
        steps: {
          ...liveTriageSteps,
          ...(currentStep
            ? {
              [currentStepId]: {
                ...currentStep,
              },
            }
            : {}),
          [nextStepId]: {
            previousStepId: currentStepId,
            questionnaire: toStoredQuestionnaire(questionnaire),
            answers: cloneSerializable(nextAnswers),
          },
        },
      },
    };

    this.persist();
  }

  public getLiveTriageAnswers(stepId: string): TriageAnswerRecord {
    const step = this.state.liveTriage?.steps[stepId];
    if (!this.isValidLiveTriageStep(step)) {
      return {};
    }

    return cloneSerializable(step.answers);
  }

  public recordLiveTriageAnswers(stepId: string, answers: TriageAnswerRecord): void {
    const step = this.state.liveTriage?.steps[stepId];
    if (!this.isValidLiveTriageStep(step)) {
      return;
    }

    this.state = {
      ...this.state,
      liveTriage: {
        activeStepId: this.state.liveTriage?.activeStepId,
        steps: {
          ...this.state.liveTriage?.steps,
          [stepId]: {
            ...step,
            answers: cloneSerializable(answers),
          },
        },
      },
    };

    this.persist();
  }

  public getAccumulatedLiveTriageAnswers(stepId: string): TriageAnswerRecord {
    const liveTriage = this.state.liveTriage;
    if (!liveTriage) {
      return {};
    }

    const liveTriageSteps: LiveTriageDraftState['steps'] = liveTriage.steps;

    const stepIds: string[] = [];
    let currentStepId: string | undefined = stepId;

    while (currentStepId) {
      const step: LiveTriageStepState | undefined = liveTriageSteps[currentStepId];
      if (!this.isValidLiveTriageStep(step)) {
        return {};
      }

      stepIds.unshift(currentStepId);
      currentStepId = step.previousStepId;
    }

    return stepIds.reduce<TriageAnswerRecord>((answers, currentId) => ({
      ...answers,
      ...cloneSerializable(liveTriageSteps[currentId].answers),
    }), {});
  }

  public hasLiveTriageState(): boolean {
    return this.state.liveTriage !== undefined;
  }

  public canMoveToPreviousLiveTriageStep(): boolean {
    return Boolean(this.getActiveLiveTriageStep()?.previousStepId);
  }

  public moveToPreviousLiveTriageStep(): boolean {
    const activeStep = this.getActiveLiveTriageStep();
    if (!activeStep?.previousStepId) {
      return false;
    }

    this.state = {
      ...this.state,
      liveTriage: {
        ...(this.state.liveTriage ?? { steps: {} }),
        activeStepId: activeStep.previousStepId,
      },
    };

    this.persist();
    return true;
  }

  public recordResponse(path: string, response: JourneyResponse): void {
    this.state = {
      ...this.state,
      steps: {
        ...this.state.steps,
        [path]: {
          ...this.state.steps[path],
          response,
        },
      },
    };

    this.persist();
  }

  public markCorruptedState(): void {
    this.state = createInitialState('We restarted this journey because your previous answers could not be restored safely.');
    this.persist();
  }

  public restore(snapshot: JourneyDraftState): void {
    this.state = cloneState(snapshot);
    this.persist();
  }

  public recordForwardTransition(currentPath: string, nextPath: string, response?: JourneyResponse): void {
    this.state = {
      ...this.state,
      steps: {
        ...this.state.steps,
        [currentPath]: {
          ...this.state.steps[currentPath],
          response: response ?? this.state.steps[currentPath]?.response,
        },
        [nextPath]: {
          ...this.state.steps[nextPath],
          previousPath: currentPath,
        },
      },
    };

    this.persist();
  }

  private hydrate(): JourneyDraftState {
    try {
      const stored = globalThis.sessionStorage.getItem(storageKey);
      if (!stored) {
        return createInitialState();
      }

      const parsed = JSON.parse(stored) as unknown;
      if (!isRecord(parsed) || !isRecord(parsed.steps)) {
        return createInitialState('We restarted this journey because your previous answers could not be restored safely.');
      }

      if (parsed.liveTriage !== undefined && !isLiveTriageDraftState(parsed.liveTriage)) {
        return createInitialState('We restarted this journey because your previous answers could not be restored safely.');
      }

      return {
        recoveryMessage: typeof parsed.recoveryMessage === 'string' ? parsed.recoveryMessage : undefined,
        steps: parsed.steps as JourneyDraftState['steps'],
        liveTriage: parsed.liveTriage,
      };
    } catch {
      return createInitialState('We restarted this journey because your previous answers could not be restored safely.');
    }
  }

  private isValidLiveTriageStep(step: LiveTriageStepState | undefined): step is LiveTriageStepState {
    return isRecord(step)
      && isRecord(step.questionnaire)
      && Array.isArray(step.questionnaire.questions)
      && hasValidWorkflowStepIdentity(step.questionnaire)
      && isRecord(step.answers);
  }

  // Returns true if making nextStepId the new active step with currentStepId as its
  // previousStepId would introduce a cycle. This happens when nextStepId is already
  // present anywhere in the current ancestry chain of currentStepId (i.e. nextStepId
  // is an ancestor of currentStepId), meaning linking currentStepId → nextStepId
  // would form a loop. The controller can use this to decide whether to call
  // initializeLiveTriageStep (fresh start) vs advanceLiveTriageStep (continuation).
  public wouldAdvanceCreateCycle(currentStepId: string, nextStepId: string): boolean {
    const steps = this.state.liveTriage?.steps;
    if (!steps) {
      return false;
    }

    const visited = new Set<string>();
    let ancestorId: string | undefined = currentStepId;

    while (ancestorId) {
      if (visited.has(ancestorId)) {
        // Existing cycle in the chain — treat as would-create-cycle to be safe.
        return true;
      }

      visited.add(ancestorId);

      if (ancestorId === nextStepId) {
        return true;
      }

      ancestorId = steps[ancestorId]?.previousStepId;
    }

    return false;
  }

  private persist(): void {
    globalThis.sessionStorage.setItem(storageKey, JSON.stringify(this.state));
  }
}